import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Loader2, Plus, Trash2, Upload } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  loadOwnPublicProfile,
  publicProfilePath,
  PUBLIC_PROFILE_LIMITS,
  type PublicProfileRow,
  type SocialLink,
  upsertOwnPublicProfile,
  uploadPublicAvatar,
  validateSocialLinks,
} from "@/lib/publicProfile";
import { useUsername } from "@/hooks/useUsername";

export function PublicProfileCard() {
  const { username, loading: usernameLoading } = useUsername();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<PublicProfileRow | null>(null);
  const [isPublic, setIsPublic] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [links, setLinks] = useState<SocialLink[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let alive = true;
    loadOwnPublicProfile()
      .then((p) => {
        if (!alive) return;
        setProfile(p);
        setIsPublic(p?.is_public ?? false);
        setDisplayName(p?.display_name ?? "");
        setBio(p?.bio ?? "");
        setAvatarUrl(p?.avatar_url ?? null);
        setLinks(p?.social_links ?? []);
      })
      .catch((e) => toast.error(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const onFile = async (file: File | null) => {
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadPublicAvatar(file);
      setAvatarUrl(url);
      toast.success("Avatar uploaded — don't forget to save");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    const linkErr = validateSocialLinks(links);
    if (linkErr) return toast.error(linkErr);
    if (bio.length > PUBLIC_PROFILE_LIMITS.bio) {
      return toast.error(`Bio must be ${PUBLIC_PROFILE_LIMITS.bio} chars or fewer`);
    }
    if (isPublic && !username) {
      return toast.error("Pick a username first to make your profile public");
    }
    setSaving(true);
    try {
      await upsertOwnPublicProfile({
        is_public: isPublic,
        display_name: displayName.trim() || null,
        bio: bio.trim() || null,
        avatar_url: avatarUrl,
        social_links: links,
      });
      toast.success("Public profile saved");
      setProfile({
        user_id: profile?.user_id ?? "",
        is_public: isPublic,
        display_name: displayName.trim() || null,
        bio: bio.trim() || null,
        avatar_url: avatarUrl,
        social_links: links,
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const addLink = () => {
    if (links.length >= PUBLIC_PROFILE_LIMITS.socialLinks) return;
    setLinks([...links, { label: "", url: "" }]);
  };
  const updateLink = (i: number, patch: Partial<SocialLink>) =>
    setLinks(links.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const removeLink = (i: number) =>
    setLinks(links.filter((_, idx) => idx !== i));

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle>Public profile</CardTitle>
        <p className="text-sm text-muted-foreground">
          Opt in to publish a simple page at{" "}
          {username ? (
            <code className="text-foreground">/u/{username}</code>
          ) : (
            <em>your future username</em>
          )}
          . Hidden from search engines.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {loading || usernameLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <>
            <div className="flex items-center justify-between rounded-md border border-border bg-muted/30 p-3">
              <div className="space-y-0.5">
                <p className="text-sm font-medium">Make my profile public</p>
                <p className="text-xs text-muted-foreground">
                  Anyone with the link can view it. You can turn this off any time.
                </p>
              </div>
              <Switch
                checked={isPublic}
                onCheckedChange={setIsPublic}
                disabled={!username}
              />
            </div>

            <div className="flex items-center gap-4">
              <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full border border-border bg-muted text-2xl font-semibold text-muted-foreground">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
                ) : (
                  <span>{(displayName || username || "?").slice(0, 1).toUpperCase()}</span>
                )}
              </div>
              <div className="space-y-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => onFile(e.target.files?.[0] ?? null)}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="mr-1 h-4 w-4" />
                  )}
                  Upload avatar
                </Button>
                {avatarUrl ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setAvatarUrl(null)}
                  >
                    Remove
                  </Button>
                ) : null}
                <p className="text-xs text-muted-foreground">PNG/JPG, ≤2 MB.</p>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="dn">Display name</Label>
              <Input
                id="dn"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                maxLength={PUBLIC_PROFILE_LIMITS.displayName}
                placeholder="e.g. Alice Chen"
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="bio">Bio</Label>
                <span className="text-xs text-muted-foreground">
                  {bio.length}/{PUBLIC_PROFILE_LIMITS.bio}
                </span>
              </div>
              <Textarea
                id="bio"
                rows={3}
                value={bio}
                onChange={(e) => setBio(e.target.value.slice(0, PUBLIC_PROFILE_LIMITS.bio))}
                placeholder="A sentence or two about you and what you publish."
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Social links</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={addLink}
                  disabled={links.length >= PUBLIC_PROFILE_LIMITS.socialLinks}
                >
                  <Plus className="mr-1 h-3 w-3" /> Add
                </Button>
              </div>
              {links.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  Up to {PUBLIC_PROFILE_LIMITS.socialLinks} links (website, X, LinkedIn, etc.).
                </p>
              ) : (
                <div className="space-y-2">
                  {links.map((l, i) => (
                    <div key={i} className="flex gap-2">
                      <Input
                        value={l.label}
                        onChange={(e) => updateLink(i, { label: e.target.value })}
                        placeholder="Label"
                        className="max-w-[10rem]"
                      />
                      <Input
                        value={l.url}
                        onChange={(e) => updateLink(i, { url: e.target.value })}
                        placeholder="https://…"
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => removeLink(i)}
                        aria-label="Remove link"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
              <Button onClick={save} disabled={saving}>
                {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                Save public profile
              </Button>
              {profile?.is_public && username ? (
                <a
                  href={publicProfilePath(username)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-primary underline"
                >
                  View public profile <ExternalLink className="ml-0.5 inline h-3 w-3" />
                </a>
              ) : null}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
